/**
 * **S7 — the to-hit draw.** The seventh stage of `attack.ts`'s `resolveSingleTarget` pipeline: the
 * attack roll, and the outcome it proposes (`event-engine-to-hit`, `decisions/0212`).
 *
 * The caller raised the {@link AttackAttemptEvent} (with Invisible's coin as its replacement);
 * this stage raises the attack roll (a `roll` event, `events/rollEvents.ts`), whose replacements
 * (Spotter's advantage) rewrite it before it is drawn, and proposes the {@link AttackOutcomeEvent}.
 * The caller raises the outcome: its replacements (Hunker Down, Barrel Roll, Valiant Aid, from
 * `toHitReactionRegistry.ts`) rewrite it before it lands, its landing grounds the `to_hit` step once
 * through {@link ToHitDraw.ground}, and its triggers are the rest of the attack (the weapon-fire
 * bookkeeping, damage, the on-hit and on-miss fires), then the reactions to it (Turret Attack, Ace's
 * Acrobatics, the bystanders). Nothing edits the step after it is built.
 *
 * The roll is proposed by {@link proposedAttackRoll}, which the attack forecast also reads
 * (`attackForecast.ts`), drawing nothing: the forecast's net accuracy and advantage are this roll's.
 */

import type { AttackFx, Combatant, MissChanceStep, ToHitStep, WeaponProfile } from "@lancer-vtt/domain";
import type { RequestAttack } from "@lancer-vtt/shared";
import { rollD20, type DiceSource } from "../../dice";
import type { LoadedOverlay } from "../effects";
import { extractBaseD20 } from "./damage";
import type { EventLoop, Happened, Replacement } from "../events/loop";
import { drawRoll, proposeRoll, type AttackAttemptEvent, type AttackOutcomeEvent, type RollEvent } from "../events/rollEvents";

/**
 * One settled attack roll: the base d20 (plus advantage's dropped die), the accuracy/difficulty
 * pool's net contribution, and the hit/crit verdict derived from them.
 *
 * **Both the original roll and Valiant Aid's reroll go through {@link rollToHit}**, which is what
 * makes "the reroll redraws the same shape of roll" a property of the code rather than a claim
 * about it — the two draws cannot drift apart by construction.
 */
export type ToHitRoll = {
  /** The KEPT base d20 face (under advantage, the higher of the two). */
  rawD20: number;
  /**
   * The DROPPED base d20 (advantage's "roll twice" — the other die, witnessed for the
   * audit trail). `undefined` without advantage.
   */
  advantageDrop: number | undefined;
  /** The accuracy/difficulty pool's signed contribution (`0` when no dice were drawn). */
  accuracyMod: number;
  /** `rawD20 + bonus + accuracyMod`. */
  total: number;
  isHit: boolean;
  isCrit: boolean;
  result: ToHitStep["result"];
};

/**
 * **S7's first pure sub-helper — the draw.** Rolls the whole attack expression through the
 * caller's recording {@link DiceSource} (the base d20, or two under advantage, plus the
 * `netAcc`-sized accuracy/difficulty pool) and settles it against the already-derived `defense`.
 *
 * Hit = meet-or-beat ("equal or exceed the target's defense"). Crit = total ≥ 20
 * AND a hit ("on a roll of 20+ you perform a critical hit"). Confirmed vs rulebook.
 * No-crit gate (`npc-turn-control` decision #5 — RAW p.281 "No critical hits — except
 * Veterans and Ultras"): an attacker frozen `base.noCrits` never crits — a would-be nat-19/20
 * resolves as a plain hit. Absence (every mech, and a Veteran/Ultra NPC) is untouched.
 * Test authors, note: crit is `total >= 20`, NOT a natural-20 d20 face — so `attackBonus`
 * stacked on top of the scripted d20 can silently cross the threshold. Budget a scripted-dice
 * source that provisions the extra crit-damage draws a `total >= 20` hit will consume, or a
 * tightly-sized `scriptedDiceSource` array under-provisions and the roll starves.
 */
export function rollToHit(opts: {
  dice: DiceSource;
  /**
   * Net accuracy — a property of the SITUATION, not of the draw, which is why the reroll is
   * handed the SAME value rather than re-deriving it (a consumable accuracy source must not be
   * re-spent by a reroll; see {@link drawToHit}'s reroll block).
   */
  netAcc: number;
  /** An advantage replacement left the roll at two d20s — "roll twice, keep the highest." */
  advantage: boolean;
  /** The attacker's to-hit bonus (the weapon's own, else the attacker's Grit). */
  bonus: number;
  /** The already-derived defense number this roll is checked against (Evasion or E-DEFENSE). */
  defense: number;
  /** The attacker's frozen `base.noCrits` — a Grunt/Standard NPC never crits. */
  noCrits: boolean | undefined;
}): ToHitRoll {
  const { dice, netAcc, advantage, bonus, defense, noCrits } = opts;
  const d20Result = rollD20(dice, netAcc, advantage);
  const draw = extractBaseD20(d20Result, advantage);
  const rawD20 = draw.rawD20;
  const advantageDrop = draw.advantageDrop;
  const accuracyMod = d20Result.accuracyMod ?? 0;
  const total = rawD20 + bonus + accuracyMod;
  const isHit = total >= defense;
  const isCrit = isHit && total >= 20 && !noCrits;
  const result: ToHitStep["result"] = !isHit ? "miss" : isCrit ? "crit" : "hit";
  return { rawD20, advantageDrop, accuracyMod, total, isHit, isCrit, result };
}

/**
 * The attack's to-hit bonus: the profile's own `attackBonus`, else the attacker's `base.attackBonus`
 * (a mech's Grit). The profile's own is an NPC weapon's tier-resolved bonus, a synthetic NPC melee
 * action's tier bonus, or — for a **tech attack** — the attacker's Tech Attack / Systems, which
 * `techAttackProfile` (`./weapons`) stamps there (`techAttackBonus`). Shared by {@link drawToHit}
 * and the pre-roll forecast (`attackForecast.ts`) so the two cannot drift.
 */
export function toHitBonus(attacker: Combatant, weapon: WeaponProfile): number {
  return weapon.attackBonus ?? attacker.base.attackBonus ?? 0;
}

/** Args for {@link defenseFor} (max-params-3 discipline, `CODE_STYLE.md`). */
export type DefenseForArgs = {
  target: Combatant;
  /** `intent.tech` — a tech attack (INVADE) rolls vs E-DEFENSE. */
  tech: boolean;
  /** The firing profile's frozen `smart` flag (Smart only applies when `tech` is false). */
  smart: boolean;
  /** S3's Stunned Evasion cap — `undefined` when nothing caps it. */
  evasionCap: number | undefined;
};

/**
 * **Which defence the attack rolls against, and its value** — extracted from {@link drawToHit}
 * (`target-hover-forecast` 1.1) so the pre-roll forecast reads the SAME choice the roll does.
 * `cappedFrom` is the target's uncapped Evasion, present only when Stunned's cap lowered it.
 */
export function defenseFor(args: DefenseForArgs): { vs: "evasion" | "edef"; value: number; cappedFrom?: number } {
  const { target, tech, smart, evasionCap } = args;
  // Smart (`weapon-tag-mechanics` Phase 2, RAW Core p.105): "all attacks with this
  // weapon — including melee and ranged — use the target's E-DEFENSE instead of
  // EVASION." A two-line branch extension (decision 10), NOT a tech attack — `isSmart`
  // is gated `!intent.tech` so a genuine tech attack (INVADE) always wins the vs-edef
  // selection through its own path, never Smart's; Smart never inherits tech-immunity,
  // INVADE routing, or the tech cover exemption ({@link
  // import("./attackModifiers").foldAttackCover} already keys on `!intent.tech` alone, so it
  // stays live for a Smart ranged attack; `skipWeaponDamage` in `attack.ts`'s damage branch
  // keys on `intent.tech` only, so Smart's damage is untouched).
  const isSmart = !tech && smart;
  const vs = tech || isSmart ? ("edef" as const) : ("evasion" as const);
  // Stunned's Evasion cap (P2.2, RAW Core p.78 "max 5 EVASION") — weapon attacks only;
  // a tech or Smart attack checks E-DEFENSE, untouched by this facet.
  const cappedEvasion = evasionCap !== undefined ? Math.min(target.base.evasion, evasionCap) : target.base.evasion;
  // "Targets with no E-DEFENSE count as having 8 E-DEFENSE" (decision 11) — `edef <= 0`
  // is the "absent" signal ONLY inside the Smart branch (an NPC's frozen `edef` falls
  // back to 0 via `readNpcStat`'s `fallback: 0` when the stat block carries none). A
  // tech attack vs an edef-0 target is untouched here — that's `shutdown-bootup-
  // selfdestruct`'s to own, never generalized from this Smart-only fallback.
  const defense = tech ? target.base.edef : isSmart ? (target.base.edef <= 0 ? 8 : target.base.edef) : cappedEvasion;
  return { vs, value: defense, ...(vs === "evasion" && cappedEvasion < target.base.evasion ? { cappedFrom: target.base.evasion } : {}) };
}

/** Named args for {@link proposedAttackRoll}. */
export type ProposedAttackRollArgs = {
  attacker: Combatant;
  target: Combatant;
  intent: RequestAttack;
  weapon: WeaponProfile;
  overlay: LoadedOverlay | undefined;
  techAttack: boolean;
  attackerAccuracy: number;
  targetAccuracy: number;
  coverAcc: number;
  /** S3's attack-roll replacements (Spotter's advantage). */
  attackRoll: readonly Replacement[];
};

/**
 * **The attack roll as proposed, then as its replacements leave it** — drawn by {@link drawToHit},
 * read without drawing by the forecast (its dry run, `attackForecast.ts`), so the two read one roll:
 * its net accuracy (`spec.accuracy`) and its advantage (`spec.keepHighest`).
 *
 * Net accuracy sums the declared `--accuracy`, the S3 attacker and target totals, the cover term and
 * the firing weapon's own innate accuracy (`npc-features-fielded-roster` Phase 3.2: the
 * Anti-Materiel Rifle's printed ACCURACY 2, NPC weapons only). A weapon whose own entry carries
 * `ignoreDifficulty` (Shatterhead) keeps only the sources that add Accuracy: each net-negative one
 * counts as 0.
 */
export function proposedAttackRoll(args: ProposedAttackRollArgs): { proposed: RollEvent; rolled: RollEvent } {
  const { attacker, target, intent, weapon, overlay, techAttack, attackerAccuracy, targetAccuracy, coverAcc, attackRoll } = args;
  const ignoresDifficulty = overlay?.[weapon.id]?.effects.some((e) => e.kind === "modifier" && e.mod.ignoreDifficulty === true) ?? false;
  const sources = [intent.accuracy, attackerAccuracy, targetAccuracy, coverAcc, weapon.accuracy ?? 0];
  const netAcc = sources.reduce((sum, n) => sum + (ignoresDifficulty ? Math.max(0, n) : n), 0);
  const proposed: RollEvent = {
    kind: "roll",
    roller: attacker.id,
    purpose: { kind: "attack", attacker: attacker.id, target: target.id, tech: techAttack },
    spec: { dice: [{ count: 1, sides: 20 }], accuracy: netAcc },
  };
  return { proposed, rolled: proposeRoll(proposed, attackRoll) };
}

/**
 * What {@link drawToHit} settles: the outcome the roll proposes, and what the outcome's replacements
 * and landing need from the roll.
 */
export type ToHitDraw = {
  /** The proposed {@link AttackOutcomeEvent}: the first roll's verdict, else the attempt's forced or coin verdict. */
  outcome: AttackOutcomeEvent;
  /**
   * Draw the attack again, the same way, and return the outcome rewritten to the new roll, which
   * stands (Valiant Aid). Same net accuracy and replacements, so a consumable accuracy source is
   * not re-spent, and the redraw lands right after the original's own draws.
   */
  reroll(outcome: AttackOutcomeEvent): AttackOutcomeEvent;
  /** The step a landed outcome grounds: `to_hit` from the standing roll and the final verdict, or `miss_chance` on a coin miss. */
  ground(outcome: AttackOutcomeEvent): ToHitStep | MissChanceStep;
};

/** Args for {@link drawToHit} (max-params-3 discipline, `CODE_STYLE.md`). */
export type DrawToHitArgs = {
  attacker: Combatant;
  target: Combatant;
  intent: RequestAttack;
  /** The SELECTED firing profile — its `attackBonus` and `smart` tag drive the roll's shape. */
  weapon: WeaponProfile;
  /** The entity overlay S1 resolved. */
  overlay: LoadedOverlay | undefined;
  techAttack: boolean;
  /** S2's attack fx descriptor, recorded on the `to_hit` step (`attack-animations` Phase 1). */
  fx: AttackFx;
  /** S3's attacker-side accuracy total. */
  attackerAccuracy: number;
  /** S3's target-side accuracy total. */
  targetAccuracy: number;
  /** S3's per-source accuracy breakdown, recorded on the `to_hit` step when non-empty. */
  accuracyContributions: { name: string; carrier: string; side: "attacker" | "target"; value: number }[];
  /** S3's Stunned Evasion cap — `undefined` when nothing capped it. */
  evasionCap: number | undefined;
  /** S3's attack-roll replacements (Spotter's advantage): they rewrite the roll before it is drawn, the original and a reroll alike. */
  attackRoll: readonly Replacement[];
  /** S2.5's cover accuracy term. */
  coverAcc: number;
  /** S2.5's cover degree, recorded on the `to_hit` step when it isn't `"none"`. */
  coverDegree: "none" | "soft" | "hard";
  /** The authority's recording dice source — the roll and any reroll both draw from it. */
  dice: DiceSource;
  /** The intent's event loop: the rolls are raised into it. */
  loop: EventLoop;
  /** The landed {@link AttackAttemptEvent}: its `forced` or coin skips the d20, and the rolls are caused by it. */
  attempt: Happened;
};

/**
 * **S7 — the to-hit draw.** Rolls the attack (unless the attempt is forced or Invisible's coin
 * missed it) and proposes the {@link AttackOutcomeEvent}; the caller raises the outcome with its
 * replacements (`toHitReactionRegistry.ts`) and grounds it through {@link ToHitDraw.ground}.
 */
export function drawToHit(args: DrawToHitArgs): ToHitDraw {
  const { attacker, target, intent, weapon, overlay, dice, loop, attempt } = args;
  const { techAttack, attackerAccuracy, targetAccuracy, accuracyContributions, evasionCap, attackRoll: replacements, coverAcc, coverDegree } = args;

  // To-hit: d20 + attackBonus ± net accuracy. A weapon (ranged/melee) attack is checked vs
  // **Evasion** (A1); a **tech attack** (A6 — INVADE; `intent.tech`) is checked vs **E-DEFENSE**
  // (and, per {@link import("./attackModifiers").foldAttackCover}, never carries a cover term).
  // The to-hit bonus is {@link toHitBonus}: a weapon attack uses the profile's own bonus (an NPC
  // weapon's tier-resolved bonus) or the attacker's `attackBonus` (a mech's Grit); a tech attack's
  // profile carries the attacker's Tech Attack / Systems, stamped by `techAttackProfile` (./weapons).
  const bonus = toHitBonus(attacker, weapon);
  const weaponAccuracy = weapon.accuracy ?? 0;
  const { proposed: attackProposal } = proposedAttackRoll({ attacker, target, intent, weapon, overlay, techAttack, attackerAccuracy, targetAccuracy, coverAcc, attackRoll: replacements });

  const { vs, value: defense } = defenseFor({ target, tech: intent.tech === true, smart: weapon.smart ?? false, evasionCap });

  // One attack roll, raised as a `roll` event caused by the attempt. Both the original roll and
  // Valiant Aid's reroll go through here, so the two draw the same shape of roll by construction.
  const attackRoll = (): { roll: ToHitRoll; id?: string } => {
    const drawn = drawRoll({
      loop,
      dice,
      cause: { event: attempt.id },
      roll: { ...attackProposal, spec: { ...attackProposal.spec } },
      replacements,
      draw: (d, roll) => rollToHit({ dice: d, netAcc: roll.spec.accuracy, advantage: roll.spec.keepHighest !== undefined, bonus, defense, noCrits: attacker.base.noCrits }),
      facts: (r) => ({ accuracyMod: r.accuracyMod }),
    });
    return { roll: drawn.result, ...(drawn.id !== undefined ? { id: drawn.id } : {}) };
  };

  // A declared-window reaction (Interdiction Point) already made this attack miss, a forced attack
  // with `autoHit` (the Autopod) hits without a roll and is never a crit, and Invisible's coin on its
  // miss face makes it miss outright: no d20 is drawn for any of them. A miss runs every miss-side
  // read (Reliable's floor, the weapon-fire bookkeeping, the on-miss fires).
  const { forced, coin } = attempt.event as AttackAttemptEvent;
  const coinMissed = coin !== undefined && coin.face === 1;
  const interdictedBy = !coinMissed && forced?.verdict === "miss" ? forced.by : undefined;
  const autoHit = !coinMissed && forced?.verdict === "hit";
  const noRoll = (hit: boolean): ToHitRoll => ({ rawD20: 0, advantageDrop: undefined, accuracyMod: 0, total: bonus, isHit: hit, isCrit: false, result: hit ? "hit" : "miss" });
  const first: { roll: ToHitRoll; id?: string } = coinMissed || interdictedBy !== undefined ? { roll: noRoll(false) } : autoHit ? { roll: noRoll(true) } : attackRoll();

  // The roll that stands: the first, until a reroll replaces it.
  let standing = first.roll;
  const proposed = (r: { roll: ToHitRoll; id?: string }): AttackOutcomeEvent => ({
    kind: "attack_outcome",
    attacker: attacker.id,
    target: target.id,
    verdict: r.roll.result,
    ...(r.id !== undefined ? { roll: r.id } : {}),
    d20: r.roll.rawD20,
    natural20: r.roll.rawD20 === 20,
    total: r.roll.total,
    ...(forced !== undefined && !coinMissed ? { forced } : {}),
    ...(coinMissed ? { missChance: { coin: coin.face, chance: coin.chance } } : {}),
  });

  return {
    outcome: proposed(first),
    reroll: (outcome) => {
      const next = attackRoll();
      standing = next.roll;
      return { ...outcome, ...proposed(next) };
    },
    ground: (outcome) => {
      if (outcome.missChance !== undefined) return { op: "miss_chance", attacker: attacker.id, target: target.id, coin: outcome.missChance.coin, chance: outcome.missChance.chance };
      return {
        op: "to_hit",
        attacker: attacker.id,
        target: target.id,
        d20: standing.rawD20,
        accuracy: standing.accuracyMod,
        // Record the status contributions to net accuracy so the log is auditable —
        // the Impaired/Lock On math is never silently folded into one number (3.1). Sourced
        // from the overlay modifier fold now (the status table is retired — Phase 8).
        statusAccuracy: {
          intent: intent.accuracy,
          attacker: attackerAccuracy,
          target: targetAccuracy,
          ...(coverAcc !== 0 ? { cover: coverAcc } : {}),
          ...(weaponAccuracy !== 0 ? { weapon: weaponAccuracy } : {}),
        },
        // Phase 2.2: the per-source breakdown behind `statusAccuracy.attacker`/`.target` —
        // omitted entirely (not an empty array) when nothing folded. The weapon's own innate
        // accuracy (3.2) rides the SAME per-source list — attacker-side, carrier = the attacker.
        ...(accuracyContributions.length > 0 || weaponAccuracy !== 0
          ? {
              accuracyContributions: [
                ...accuracyContributions,
                ...(weaponAccuracy !== 0 ? [{ name: weapon.id, carrier: attacker.id, side: "attacker" as const, value: weaponAccuracy }] : []),
              ],
            }
          : {}),
        bonus,
        total: standing.total,
        vs,
        defense,
        ...(!intent.tech && evasionCap !== undefined ? { evasionCap } : {}),
        result: outcome.verdict,
        ...(coverDegree !== "none" ? { cover: coverDegree } : {}),
        ...(standing.advantageDrop !== undefined ? { advantageDrop: standing.advantageDrop } : {}),
        ...(interdictedBy !== undefined ? { forcedMiss: { interdictedBy } } : {}),
        ...(autoHit ? { autoHit: true as const } : {}),
        fx: args.fx,
      };
    },
  };
}
